import { Stack } from '@mui/joy';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthActions } from 'auth/hooks';
import { userService } from 'auth/services';
import { IUserAutocomplete } from 'auth/types';
import { Form, FormField } from 'core/components';
import { TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { Level } from 'theme/enums';

import { MostRecent } from './MostRecent';
import { switchUsersSchema } from './schema';
import { ISwitchUserFormData } from './type';
import { getUserIdAndName } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SwitchUserForm = ({ isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { impersonate, addRecentImpersonatedUser } = useAuthActions();

  const onSubmit =
    (reset: UseFormReset<ISwitchUserFormData>) => async (data: ISwitchUserFormData) => {
      const { success, impersonatedUser } = await impersonate({
        impersonatedUserId: data.user.id,
      });

      if (!success) {
        return;
      }

      addRecentImpersonatedUser(impersonatedUser);

      setTimeout(() => handleOnClose(reset), 1000);

      window.location.reload();
    };

  const handleOnClose = (reset: UseFormReset<ISwitchUserFormData>) => {
    reset();
    onClose();
  };

  const renderOptions = (option: IUserAutocomplete, _, inputValue) => {
    return (
      <Stack spacing={0}>
        <TextHighlighter
          text={getUserIdAndName(option)}
          highlight={inputValue}
          level={Level.TITLE_SM}
        />

        <TextHighlighter text={option?.email} highlight={inputValue} level={Level.BODY_XS} />
      </Stack>
    );
  };

  return (
    <FormProvider schema={switchUsersSchema()}>
      <Form
        title={t('labelImpersonate')}
        isOpen={isOpen}
        onClose={handleOnClose}
        submitBtnLabel={t('buttonLabelImpersonate')}
        onSubmit={onSubmit}
      >
        <FormField
          name="user"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelUser'),
            getOptions: userService.getManyAutocomplete,
            renderOptions,
            getLabel: (opt: IUserAutocomplete) => getUserIdAndName(opt),
          }}
        />

        <MostRecent onSubmit={onSubmit} />
      </Form>
    </FormProvider>
  );
};
